/**
 * Component: Carousel
 * ------------------------------------------------------------------------------
 * An interface to create carousel objects using the Flickity plugin.
 * - https://flickity.metafizzy.co/
 *
 * @namespace carousel
 */
import debounce from 'lodash-es/debounce';
import Flickity from 'flickity';

import {extendDefaults, on, isElementInViewport} from '../helpers/utils';

require('flickity-imagesloaded');
require('flickity-as-nav-for');

/**
 * DOM selectors.
 */
const selectors = {
  cell: '[js-carousel="cell"]',
  container: '[js-carousel="container"]',
};

let performanceEnhancerInit = false;

/**
 * Create a new carousel instance.
 * @param {String} selector - DOM selector.
 * @param {object} config - Flickity carousel settings.
 */
export default (selector, config) => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selector),
    cells: [...document.querySelectorAll(config.cellSelector)],
  };

  /**
   * Instance default settings.
   */
  const namespace = nodeSelectors.container.id || selector;
  const defaults = {
    aria: true,
    arrowShape: 'M47.02 0 51 3.978 7.96 47 51 90.022 47.02 94 0 47z',
    asNavFor: null,
    autoPlay: false,
    cellAlign: 'left',
    imagesLoaded: true,
    pageDots: false,
    pauseAutoPlayOnHover: false,
    prevNextButtons: false,
    wrapAround: true,
    contain: true,
    draggable: true,
    groupCells: false,
    on: {
      ready: () => {

        /**
         * Offsetting event emitter to trigger.
         */
        window.setTimeout(() => {
          Frame.EventBus.emit(`Carousel-${namespace}:ready`, {
            container: nodeSelectors.container,
          });
        }, 0);
      },
      settle: () => {
        Frame.EventBus.emit(`Carousel-${namespace}:settle`, {
          container: nodeSelectors.container,
        });
      },
    },
  };

/**
   * Instance settings.
   */
  const settings = extendDefaults(defaults, config);

  /**
   * Instance globals.
   */
  let carousel = {};

  /**
   * Initialise carousel component.
   */
  function init() {
    if (nodeSelectors.cells.length <= 1) {
      return;
    }

    carousel = new Flickity(nodeSelectors.container, {
      arrowShape: settings.arrowShape,
      asNavFor: settings.asNavFor,
      autoPlay: settings.autoPlay,
      cellSelector: settings.cellSelector,
      cellAlign: settings.cellAlign,
      on: settings.on,
      pageDots: settings.pageDots,
      pauseAutoPlayOnHover: settings.pauseAutoPlayOnHover,
      prevNextButtons: settings.prevNextButtons,
      wrapAround: settings.wrapAround,
      imagesLoaded: settings.imagesLoaded,
      contain: settings.contain,
      draggable: settings.draggable,
      groupCells: settings.groupCells,
    });

    if (settings.aria) {
      setARIAEventListeners();
    }

    if (performanceEnhancerInit === false) {
      performanceEnhancer();
    }
  }

  /**
   * Set listeners for Carousel and update accessibility markup.
   */
  function setARIAEventListeners() {
    Frame.EventBus.listen(`Carousel-${namespace}:ready`, (section) => {
      updateVisibleCells(section);
      on('resize', debounce(() => updateVisibleCells(section), 500));
    });

    Frame.EventBus.listen(`Carousel-${namespace}:settle`, (section) => {
      updateVisibleCells(section);
    });
  }

  /**
   * Sets tabindex to -1 to links from slides that are not active.
   * @param {HTMLElement} section - Flickity slider.
   */
  function updateVisibleCells(section) {
    const {container} = section;
    const containerViewport = container.querySelector('.flickity-viewport');

    if (!containerViewport) { return; }

    [...container.querySelectorAll(selectors.cell)].forEach((cell) => {
      if (!isElementInViewport(containerViewport, cell)) {
        updateCellARIA(cell, true, -1);
        return;
      }

      updateCellARIA(cell);
    });
  }

  /**
   * Checks if slide is within Flickity viewport and updates the tabbable
   * elements tabindex value.
   * @param {HTMLElement} cell - Flickity slide.
   * @param {Boolean} isHidden - If slide is not visible in viewport.
   * @param {Number} tabindexValue - Tabindex value.
   */
  function updateCellARIA(cell, isHidden = false, tabindexValue = 0) {
    const tabbableElements = cell.querySelectorAll('a, button');

    cell.setAttribute('aria-hidden', isHidden);

    if (tabbableElements.length) {
      [...tabbableElements].forEach((link) => {
        link.setAttribute('tabindex', tabindexValue);
      });
    }
  }

  /**
   * Trigger reload events.
   */
  function reload() {
    carousel.reloadCells();
  }

  /**
   * Trigger resize events.
   */
  function resize() {
    carousel.resize();
  }

  /**
   * Destroy instance.
   */
  function destroy() {
    if (Object.keys(carousel).length === 0) {
      return;
    }

    carousel.destroy();
  }

  /**
   * Updated selected slide using index.
   * @param {Number} index - Image slide index to go to.
   */
  function select(index) {
    carousel.select(Number(index));
  }

    /**
   * Improves performance of sliders on IOS devices
   */
  function performanceEnhancer() {
    performanceEnhancerInit = true;
    let touchingCarousel = false;
    let touchStartCoords;

    document.body.addEventListener('touchstart', (event) => {
      if (event.target.closest('.flickity-slider')) {
        touchingCarousel = true;
      } else {
        touchingCarousel = false;
        return;
      }

      touchStartCoords = {
        x: event.touches[0].pageX,
        y: event.touches[0].pageY,
      };
    });

    document.body.addEventListener('touchmove', (event) => {
      if (!(touchingCarousel && event.cancelable)) {
        return;
      }

      const moveVector = {
        x: event.touches[0].pageX - touchStartCoords.x,
        y: event.touches[0].pageY - touchStartCoords.y,
      };

      if (Math.abs(moveVector.x) > 7) {
        event.preventDefault();
      }

    }, {passive: false});
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    destroy,
    init,
    reload,
    resize,
    select,
  });
};
